import { sentryService } from '@/services/telemetry/sentry';
import { shortenLocaleCode } from './locale-matcher';
import { resetMissingKeyCache } from './missing-handler';

const LOCALE_CLASS_PREFIX = 'App--locale-';
const LANG_CLASS_PREFIX = 'App--lang-';

/**
 * Atomically update the locale/language CSS classes on `:root`. Builds target
 * names first, then removes stale classes and adds targets in one synchronous
 * block — no FOUC, and rapid sequential calls can't leave duplicated stale
 * classes behind.
 */
export function setGlobalClassName(localeCode: string): void {
  const root = document.querySelector(':root');
  if (!root?.classList) return;

  const targetLocaleClass = LOCALE_CLASS_PREFIX + localeCode;
  const targetLangClass = LANG_CLASS_PREFIX + shortenLocaleCode(localeCode);

  const classesToRemove: string[] = [];
  for (const className of root.classList) {
    if (
      (className.startsWith(LOCALE_CLASS_PREFIX) ||
        className.startsWith(LANG_CLASS_PREFIX)) &&
      className !== targetLocaleClass &&
      className !== targetLangClass
    ) {
      classesToRemove.push(className);
    }
  }

  for (const className of classesToRemove) {
    root.classList.remove(className);
  }
  root.classList.add(targetLocaleClass);
  root.classList.add(targetLangClass);
}

/**
 * Fire all locale-change side effects in one place: DOM class, Sentry tag,
 * missing-keys cache reset. Called from the locale watcher in `i18n/index.ts`.
 */
export function applyLocaleSideEffects(localeCode: string): void {
  setGlobalClassName(localeCode);
  sentryService.setLocale(localeCode);
  // Drop missing-keys log for the new locale — keys that WERE missing under
  // the previous locale may exist here.
  resetMissingKeyCache(localeCode);
}
